import { OnInit, Component, ViewChild, forwardRef, Input, Renderer2, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { SessionStorageService, selectorAuth } from '@app/core';
import { environment } from '@env/environment';
import { InfoService } from '@app/core/info/info.service';
import { BusinessDownloadService } from '@app/business/business-download.service';
import { HistoryService } from '@app/certificates/history/history.service';
import { BusinessRequestedHistoryInitService } from '@app/business/business-history/business-requested-history/services/business-requested-history-init.service';
import { RequestedHistory, RequestedHistoryRequest } from '@app/certificates/history/history';
import { HistoryDetailsComponent } from '@app/business/history-details/history-details.component';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { ICertificateData } from '@app/certificates/manage/manage';
import { IUserData } from '@app/profile/main/details';
import { SortableClolumsNames, DocumentStatus, DocumentStatusMap, ParcelType, DocumentTypeMap } from '@app/business/business-models';
import { IParcelTypeOptions, IStatusesAndLabelsForParcel, ParcelStatus, } from './business-requested-history.models';
import { BusinessService } from '@app/business/business.service';
import { TranslateService } from '@ngx-translate/core';



@Component({
  selector: 'cloud-business-requested-history',
  templateUrl: './business-requested-history.component.html',
  styleUrls: ['./business-requested-history.component.scss']
})

export class BusinessRequestedHistoryComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;
  @ViewChild('rejectParcelTemplate') rejectParcelTemplate!: TemplateRef<any>;
  @Input() isSubcomponent = false;
  unsubscribe$: Subject<void> = new Subject<void>();
  signingHistoryDataSource: MatTableDataSource<RequestedHistory> = new MatTableDataSource<RequestedHistory>();

  displayedColumns: string[]=[]; // Columns displayed in the table

  parcelTypeOptions: IParcelTypeOptions[]; // Options for the parcel type filter dropdown
  statusesAndLabelsForParcel: IStatusesAndLabelsForParcel[]; // Options for the batch signing and registered mail status filter dropdown

  displayOptions: RequestedHistoryRequest;
  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;
  certificateNumbers: string[];
  displayAllCertsOption = false;
  certificates: ICertificateData[];
  fromDate: string;
  toDate: string;
  minEndDateValue: string;
  maxStartDateValue: string;
  documentNumber: any;
  isLoading = true
  signedfileName: any;
  resetCertificate: string;
  userData: IUserData;
  parcel: RequestedHistory;
  rejectDialogRef: MatDialogRef<any, any>;
  downloadIds = [];
  isSuperAdmin: boolean = false;
  businessAccountId: any;
  constructor(
    public info: InfoService,
    private historyService: HistoryService,
    public tableHelper: MatTableHelper,
    private store: Store<any>,
    public dialog: MatDialog,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private initService: BusinessRequestedHistoryInitService,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private businessDownloadService: BusinessDownloadService,
    private businessService: BusinessService
  ) {
  }

  ngOnInit() {
    this.getCurrentLoggedInAccount()
    // Initialize table columns, parcel type options, batch signing status labels, and registered mail status labels
    this.displayedColumns = this.initService.setDisplayedColumns();
    this.parcelTypeOptions = this.initService.setParcelTypeOptions();
    this.statusesAndLabelsForParcel = this.initService.setParcelStatusLabels();

    this.initDisplayOptions();
    this.getUserInfo();
    this.displayOptions.status = "IN_PROGRESS";
    this.loadRequestedHistory();
  }
  getCurrentLoggedInAccount() {
    this.businessAccountId = this.sessionStorageService.getItem("businessAccountId");
    const company = this.sessionStorageService.getItem("accounts").find(item => item.cmrId === this.businessAccountId);
    this.isSuperAdmin = company?.role === 'MY_BTRUST_SUPER_ADMINISTRATOR';
  }

  getUserInfo() {
    this.store
      .select(selectorAuth)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.userData) { this.userData = res.userData; }
        if (this.userData == undefined) {
          this.userData = this.sessionStorageService.getItem("loginEventData")
        }
      });
  }

  private initDisplayOptions() {
    this.displayOptions = {};
    this.displayOptions.offset = 0;
    this.displayOptions.limit = 10;
  }

  loadRequestedHistory() {
    this.displayOptions.employeeCmrId = this.userData.cmrId;
    this.displayOptions.companyCmrId = this.sessionStorageService.getItem("businessAccountId");
    this.historyService.getRequestsHistory(this.displayOptions, true).subscribe(res => {
      if (res?.data?.content) {
        this.setDataSource(res.data.content);
        this.documentNumber = res.data.size;
        this.setPaginator();
      } else if (res?.data?.message) {
        this.info.notice(res.data.message);
        this.isLoading = false;
      } else {
        this.isLoading = false;
      }
    });
  }

  // tslint:disable-next-line:no-shadowed-variable
  setDataSource(requestedHistory: RequestedHistory[]) {
    this.isLoading = false;
    let dataSource = [];

    if (requestedHistory) {
      dataSource = requestedHistory.map(a => Object.assign({
        date: a.ts,
        downloadeTimestamp: a.downloadTs,
        packageName: a.name,
        packageNameTooltip: '',
        relyingPartyId: a.rpId,
        relyingPartyName: a.rpName,
        status: a.status,
        statusIcon: DocumentStatusMap.get(a.status as DocumentStatus),
        fileId: a.fileId,
        signCountTxt: a.signCount + '/' + a.reqSignCount,
        signCount: a.signCount,
        reqSignCount: a.reqSignCount,
        requestedId: a.id,
        messageIdentifier: a.serviceEventId,
        docCount: a.docCount,
        parcelType: a.parcelType,
        isRegisteredMail: a.parcelType === ParcelType.REGISTERED_DELIVERY,
        typeIcon: DocumentTypeMap.get(a.parcelType as ParcelType),
        isDownloadable: this.isDownloadable(a),
        isResendable: this.isResendable(a),
        isRejectable: this.isRejectable(a),
        isRegisteredMailReceiptDownloadable: this.isRegisteredMailReceiptDownloadable(a)
      }));
    }
    this.signingHistoryDataSource = new MatTableDataSource(dataSource);
  }

  hasData() {
    return (this.signingHistoryDataSource.data.length !== 0);
  }

  updatePackageNameTooltip(row: any, element: HTMLElement) {
    row.packageNameTooltip = element.scrollWidth > element.clientWidth ? row.packageName : '';
  }

  setParcelType() {
    if (this.displayOptions.parcelType === 'ALL') {
      delete this.displayOptions.parcelType;
    }

    this.displayOptions.offset = 0;
  }

  setOrderStatus() {
    if (this.displayOptions.status === 'ALL') {
      delete this.displayOptions.status;
    }

    this.displayOptions.offset = 0;
  }

  private setPaginator() {
    this.paginator.pageIndex = this.displayOptions.offset;
    this.paginator.pageSize = this.displayOptions.limit;
    this.paginator.length = this.documentNumber;
  }

  setRequestDates() {
    const pip: DatePipe = new DatePipe('bg-BG');

    if (this.fromDate) {
      this.minEndDateValue = this.fromDate;
      const startDate = new Date(this.fromDate).setHours(0, 0, 0, 0);
      this.displayOptions.fromCreatedDate = encodeURIComponent(pip.transform(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ') as string);
    }

    if (this.toDate) {
      this.maxStartDateValue = this.toDate;
      const endDate = new Date(this.toDate).setHours(23, 59, 59, 999);
      this.displayOptions.toCreatedDate = encodeURIComponent(pip.transform(endDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ') as string);
    }
    this.displayOptions.offset = 0;
  }

  searchRequestedPackage() {
    if (this.displayOptions.textToShow) {
      this.displayOptions.offset = 0;
    }
    this.loadRequestedHistory();
  }

  clearText() {
    this.displayOptions.textToShow = '';
    delete this.displayOptions.textToShow;
    this.displayOptions.offset = 0;
  }

  resetFilters() {
    this.fromDate = '';
    this.maxStartDateValue = '';
    this.toDate = '';
    this.minEndDateValue = '';
    this.resetCertificate = '';
    this.initDisplayOptions();
    this.loadRequestedHistory();
  }

  sortByHeader(sorter) {
    const columnName = this.getColumnName(sorter.id);
    if (!sorter._arrowDirection) {
      sorter._arrowDirection = 'desc';
    }
    this.displayOptions.orderByFilter = columnName;
    this.displayOptions.order = sorter._arrowDirection.toUpperCase();
    this.loadRequestedHistory();
  }


  getColumnName(name: string): string {
    switch (name) {
      case 'date':
        return SortableClolumsNames.DATE;
      case 'packageName':
        return SortableClolumsNames.TEXT_TO_SHOW;
      case 'certSerialNumber':
        return SortableClolumsNames.SERIAL_NUMBER;
      case 'relyingPartyName':
        return SortableClolumsNames.RELYING_PARTY;
      case 'signedDate':
        return SortableClolumsNames.SIGNED_DATE;
      case 'downloadeTimestamp':
        return SortableClolumsNames.DOWNLOAD_TS;
      default:
        return SortableClolumsNames.DATE;
    }
  }

  isDownloadable(packageData: RequestedHistory) {
    // Allow download for Registered mail no matter the status
    if (packageData.parcelType === ParcelType.REGISTERED_DELIVERY) {
      return true;
    }

    // Logic for bach signing
    const status = (packageData.status || '') as DocumentStatus;
    if (DocumentStatusMap.get(status)?.key == ParcelStatus.SIGNED
      || DocumentStatusMap.get(status)?.key == ParcelStatus.ARCHIVED
      || DocumentStatusMap.get(status)?.key == ParcelStatus.RECEIVED) {
      return true;
    }
    return false;
  }

  isResendable(packageData: RequestedHistory) {
    if (DocumentStatusMap.get(packageData.status as DocumentStatus)?.key == ParcelStatus.EXPIRED) {
      return true;
    } else {
      return false;
    }
  }

  isRejectable(packageData: RequestedHistory): boolean {
    return (packageData.status === ParcelStatus.SIGNING || packageData.status === ParcelStatus.IN_PROGRESS) && packageData.senderCmrId == this.userData.cmrId;
  }

  isRegisteredMailReceiptDownloadable(packageData: RequestedHistory) {
    // Only for Registered mail
    if (packageData.parcelType !== ParcelType.REGISTERED_DELIVERY) {
      return false;
    }

    // Allowed status for receipt
    if (packageData.status === DocumentStatus.SENT
      || packageData.status === DocumentStatus.DELIVERED
      || packageData.status === DocumentStatus.VIEWED
      || packageData.status === DocumentStatus.FAILED
    ) {
      return true;
    }

    return false;
  }

  onDownloadPackage(pack: any): void {
    this.businessService.getDownloadDocIds(pack.requestedId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          if (response?.data?.documentsForDownload) {
            const zip = new JSZip();
            const files = [];

            response.data.documentsForDownload.forEach(docId => {
              files.push(this.businessService.getRequestedFile(docId));
            });

            forkJoin(files)
              .subscribe({
                next: (results) => {
                  results.forEach(result => {
                    if (result.status !== 200) {
                      this.info.error(result.message);
                    } else {
                      let fileName = result.headers.get('Content-Disposition') || 'unknown';
                      fileName = fileName.substring(fileName.indexOf('=') + 2, fileName.length - 1);
                      fileName = decodeURIComponent(fileName);
                      zip.file(fileName, result.body);
                    }
                  });

                  zip.generateAsync({ type: 'blob' }).then(blob => {
                    saveAs(blob, pack.packageName + '.zip');
                  });
                },
                error: (err) => this.info.error(err.message)
              });
          }
        },
        error: (err) => this.info.error(err.message)
      });
  }


  onDownloadRegisteredMailReceipt(rowData) {
    const messageIdentifier = rowData.messageIdentifier;

    this.businessDownloadService.requestForReceiptData(messageIdentifier)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.businessDownloadService.downloadReceipt(res.data.pdfData, this.renderer);
        },
        error: (err) => {
          this.info.error(err.message);
        }
      })
  }

  onPaginateChange(paginator) {
    if (this.displayOptions) {
      this.displayOptions.limit = paginator.pageSize;
      this.displayOptions.offset = (this.displayOptions.offset || 0) + (paginator.pageIndex - paginator.previousPageIndex);
      this.loadRequestedHistory();
    }
  }

  openRequestedDetailsDialog(rowData) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = rowData;

    dialogConfig.panelClass = 'panel-class-history';

    const dialogRef = this.dialog.open(HistoryDetailsComponent, dialogConfig);
    const sub = dialogRef.componentInstance.close.subscribe(() => {
      this.dialog.closeAll();
    });
  }

  onResendPackage(pack: any) {
    this.router.navigate(['business/signing'], { state: { requestedId: pack.requestedId, signCount: pack.signCount } });
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  onRejectPackage(rejectReason: string = ''): void {
    this.businessService.rejectParcel(this.parcel.requestedId, this.userData.cmrId, rejectReason.trim())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          if((res.status / 100) === 2) {
            this.info.notice(this.translateService.instant('businessTexts.rejectSuccess'));
            this.rejectDialogRef.close();
          } else {
            this.info.error(res.message);
          }
        },
        error: (err) => {
          this.info.error(err.message);
        }
      })
  }

  openParcelRejectDialog(rowData: RequestedHistory): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = rowData;
    dialogConfig.panelClass = 'panel-class-parcel-reject';

    this.parcel = rowData;
    console.info(this.parcel);
    this.rejectDialogRef = this.dialog.open(this.rejectParcelTemplate, dialogConfig);
  }

  getTimeAbbr() {
    return this.translateService.currentLang == 'bg' ? 'г. ' : '';
  }
}
